import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, Copy, Paperclip, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { copyText } from "@/lib/clipboard";
import { whatsappUrl } from "@/lib/share";
import { BACKEND_URL } from "@/lib/backendUrl";

const API = `${BACKEND_URL}/api`;

function Seg({ val, set, opts, testid }) {
  return (
    <div className="flex rounded-lg border border-slate-700 bg-slate-950/60 p-0.5" data-testid={testid}>
      {opts.map(([k, l]) => <button key={k} data-on={val === k} onClick={() => set(k)} data-testid={`${testid}-${k}`} className="px-3 py-1.5 rounded-md text-xs font-semibold text-slate-400 data-[on=true]:bg-violet-500 data-[on=true]:text-white transition-colors">{l}</button>)}
    </div>
  );
}

// AI accounts assistant: drafts a bilingual cover / reminder, then Copy · WhatsApp · WhatsApp+PDF · Email+PDF
export default function AiDialog({ inv, shop, open, onOpenChange, onSharePdf, onEmail, defaultPurpose = "cover" }) {
  const [purpose, setPurpose] = useState(defaultPurpose);
  const [tone, setTone] = useState("friendly");
  const [language, setLanguage] = useState("zh"); // second language alongside English — only one
  useEffect(() => { // pre-select the customer's remembered language
    if (!open || !inv.customer_id) return;
    axios.get(`${API}/customers/${inv.customer_id}`).then(({ data }) => setLanguage(data.message_language || "zh")).catch(() => {});
  }, [open, inv.customer_id]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [mailing, setMailing] = useState(false);
  const email = inv.customer_email?.trim();

  const draft = async () => {
    setBusy(true); setText("");
    try {
      const { data } = await axios.post(`${API}/invoices/${inv.id}/ai-message`, { purpose, tone, language });
      setText(data.message);
    } catch (e) { toast.error("AI assistant unavailable", { description: e?.response?.data?.detail }); }
    finally { setBusy(false); }
  };
  const sendEmail = async () => {
    setMailing(true);
    try { await onEmail(text); onOpenChange(false); } finally { setMailing(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl bg-[#111827] border-slate-700 text-slate-100" data-testid="ai-message-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-extrabold flex items-center gap-2"><Sparkles className="w-5 h-5 text-violet-400" /> AI accounts assistant <span className="font-zh text-violet-300/80 text-base">AI 账务助手</span></DialogTitle>
          <DialogDescription className="text-slate-400">Drafts a bilingual message (starts with "Dear …") for {inv.invoice_no} · {inv.company || inv.customer_name}{email ? ` · ${email}` : ""}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap gap-2">
          {inv.status === "paid"
            ? <div className="rounded-md border border-emerald-600/50 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-200" data-testid="ai-purpose-receipt">Invoice is paid — drafting a <b>thank-you / receipt</b> message (no payment request). 已付款 · 感谢信</div>
            : <Seg val={purpose} set={setPurpose} opts={[["cover", "Send invoice"], ["reminder", "Payment reminder"]]} testid="ai-purpose" />}
          <Seg val={tone} set={setTone} opts={[["friendly", "Friendly"], ["formal", "Formal"], ["firm", "Firm"]]} testid="ai-tone" />
          <Seg val={language} set={setLanguage} opts={[["zh", "English + 中文"], ["ta", "English + தமிழ்"], ["ms", "English + Melayu"]]} testid="ai-language" />
          <Button onClick={draft} disabled={busy} data-testid="ai-draft-btn" className="ml-auto bg-violet-500 hover:bg-violet-400 text-white"><Sparkles className="w-4 h-4 mr-1.5" /> {busy ? "Drafting…" : text ? "Redraft" : "Draft message"}</Button>
        </div>
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={11} data-testid="ai-message-text" placeholder={busy ? "Writing in English and the chosen language…" : "Click “Draft message” — you can edit the result before sending."} className="w-full rounded-lg bg-slate-950/60 border border-slate-700 p-3 text-sm leading-relaxed" />
        {text && (
          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" onClick={async () => ((await copyText(text)) ? toast.success("Copied") : toast.error("Copy blocked"))} data-testid="ai-copy-btn" className="border-slate-700 bg-slate-900/60 text-slate-200 hover:bg-slate-800"><Copy className="w-4 h-4 mr-1.5" /> Copy</Button>
            <Button onClick={() => window.open(whatsappUrl(inv.customer_phone, shop.countryCode, text), "_blank", "noopener")} data-testid="ai-whatsapp-btn" className="bg-[#25D366] hover:bg-[#1ebe5b] text-slate-950 font-bold">WhatsApp (text)</Button>
            <Button onClick={() => { onOpenChange(false); onSharePdf(text); }} data-testid="ai-whatsapp-pdf-btn" className={`${email ? "" : "col-span-2"} bg-emerald-700 hover:bg-emerald-600 text-white font-bold`}><Paperclip className="w-4 h-4 mr-1.5" /> WhatsApp + PDF</Button>
            {email && <Button onClick={sendEmail} disabled={mailing} data-testid="ai-email-pdf-btn" className="bg-sky-600 hover:bg-sky-500 text-white font-bold"><Mail className="w-4 h-4 mr-1.5" /> {mailing ? "Sending…" : "Email + PDF"}</Button>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
